import { type FC, memo, useEffect, useMemo } from 'react';

import { Content } from '@vakantiesnl/components/src/__LEGACY__/atoms/Content';
import { Heading } from '@vakantiesnl/components/src/__LEGACY__/atoms/Heading';
import { SubMenu } from '@vakantiesnl/components/src/__LEGACY__/molecules/SubMenu';
import { RichText } from '@vakantiesnl/components/src/molecules/RichText';
import { withGlobalStateGSP } from '@vakantiesnl/components/src/utils/globalState/withGlobalState';
import { getEntryCollection } from '@vakantiesnl/services/src/contentful';
import { mapContentTemplate } from '@vakantiesnl/services/src/contentful/mapper';
import { resolveBySlug } from '@vakantiesnl/services/src/contentful/slug-resolver';
import { useHeaderContext } from '@vakantiesnl/services/src/context/headerContext';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { errorHandler } from '@vakantiesnl/services/src/util/errorHandling';
import { filterContentfulSlugFromPath } from '@vakantiesnl/services/src/util/urlUtils';
import { type ContentTemplate, type RawContentTemplate, type VaknlHeaderBlock } from '@vakantiesnl/types';
import { useRouter } from 'next/router';
import { type GetStaticPaths, type GetStaticProps, type GetStaticPropsResult } from 'next/types';

import DefaultLayout from '../../layout';
import { type GlobalPageProps, type VaknlNextPage } from '../_app';

type Props = GlobalPageProps & {
	data: ContentTemplate;
};

const RenderLayout: FC<Props> = ({ data }) => {
	const headerContext = useHeaderContext<VaknlHeaderBlock>();

	const menuItems = useMemo(
		() => (headerContext && !!Object.keys(headerContext).length ? headerContext.subMenuItems : []),
		[headerContext],
	);

	const router = useRouter();

	useEffect(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/contentPage').then(({ contentPageLoad }) => {
				track(contentPageLoad(router.asPath));
			}),
		);
	}, [router.asPath]);

	return (
		<Content>
			<SubMenu menuItems={menuItems} microCopies={useMicroCopyContext()} />
			<Content variant={data.wrapperVariant}>
				{data.title && <Heading headingLevel="h1">{data.title}</Heading>}
				<RichText document={data.content} />
			</Content>
		</Content>
	);
};

const MemoRenderLayout = memo(RenderLayout);

const ContentPage: VaknlNextPage<Props> = (props) => {
	return <MemoRenderLayout {...props} />;
};

export const getStaticProps: GetStaticProps = withGlobalStateGSP(
	(globalState) =>
		async (context): Promise<GetStaticPropsResult<Props>> => {
			try {
				if (context && context.params && Array.isArray(context.params?.slug)) {
					/** Connect with a / for incoming param slugs to support nested routes like /path1/path2 */
					const contentfulSlug = filterContentfulSlugFromPath('/' + context.params.slug.join('/'));

					const rawContent = await resolveBySlug<RawContentTemplate>(contentfulSlug, undefined);
					if (rawContent) {
						const data = mapContentTemplate(rawContent);

						return {
							props: {
								data,
								seo: data.seo,
								contentfulId: data.id,
								globalState,
							},
							revalidate: 60 * 15, //** 15 min after revalidation */
						};
					}
				}
			} catch (err) {
				if (err instanceof Error && !err.message.includes('Page not found')) {
					errorHandler.report(`Error fetching content page data: ${err}`);
					throw err;
				}
			}

			return { notFound: true };
		},
);

export const getStaticPaths: GetStaticPaths = async () => {
	/**
	 * Fetch all 'contentpage' type pages from contentful
	 * And use those pages to create a list of static paths
	 */
	const paths = await getEntryCollection<RawContentTemplate>('contentpage', {
		include: 3,
		limit: 1000,
		'fields.seo.sys.contentType.sys.id': 'seo',
	})
		.then((entry) => entry.items)
		.then((contentPages) =>
			contentPages.map((contentPage) =>
				/** Remove starting slash because this gets stringified during build and adds a unwanted extra slash */
				contentPage.fields.seo.fields.slug.replace(/^\/+/, ''),
			),
		)
		.catch((err) => {
			errorHandler.report(`Error fetching content pages ${err}`);
		});
	if (!paths) {
		errorHandler.report('Content pages could not be built');
		throw new Error('Content pages could not be built');
	}

	/**
	 * Loop over all pages per locale domain, so we can pre-build the HTML for content pages per locale
	 * Return an array of slugs to support nested routes like /path1/path2
	 */
	return {
		paths: paths.map((path) => ({ params: { slug: [path] } })),
		fallback: 'blocking' /** Any pages that are not be build on build time will be serverside rendered  */,
	};
};

ContentPage.Layout = DefaultLayout;

export default ContentPage;
